import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      nav: {
        home: 'Home',
        raffles: 'Raffles',
        create: 'Create Raffle',
        createRaffle: 'Create Raffle',
        myRaffles: 'My Raffles',
        admin: 'Admin',
        login: 'Login',
        register: 'Sign Up',
        logout: 'Logout',
        profile: 'Profile'
      },
      hero: {
        title: 'Create & Win Amazing Raffles!',
        subtitle: 'The easiest way to create, share, and participate in raffles. Pick your lucky numbers and win big!',
        cta: 'Explore Raffles',
        createCta: 'Create Your Raffle'
      },
      features: {
        title: 'Why Choose RifaMaker?',
        easy: 'Super Easy',
        easyDesc: 'Create your raffle in minutes. Set the numbers, price, and prize — we handle the rest!',
        secure: 'Secure Payments',
        secureDesc: 'Pay with PIX directly. Fast, safe, and easy payments.',
        fun: 'Fun & Social',
        funDesc: 'Share with friends, track your numbers, and celebrate wins with confetti!'
      },
      raffle: {
        create: 'Create Raffle',
        title: 'Title',
        description: 'Description',
        prize: 'Prize',
        prizeValue: 'Prize Value',
        ticketPrice: 'Ticket Price',
        totalNumbers: 'Total Numbers',
        unlimited: 'Unlimited',
        limited: 'Limited',
        numberType: 'Number Type',
        maxNumbers: 'Max Numbers',
        drawDate: 'Draw Date',
        drawTime: 'Draw Time',
        status: 'Status',
        active: 'Active',
        completed: 'Completed',
        pending: 'Pending',
        selectNumbers: 'Select Your Numbers',
        available: 'Available',
        selected: 'Selected',
        sold: 'Sold',
        reserved: 'Reserved',
        buyTickets: 'Buy Tickets',
        myNumbers: 'My Numbers',
        participants: 'Participants',
        winner: 'Winner',
        drawNow: 'Draw Now',
        shareRaffle: 'Share Raffle',
        shareCampaign: 'Share Campaign',
        image: 'Raffle Image',
        currency: 'Currency'
      },
      payment: {
        title: 'Payment',
        method: 'Payment Method',
        creditCard: 'Credit Card',
        pix: 'PIX',
        total: 'Total',
        processing: 'Processing...',
        success: 'Payment Successful!',
        error: 'Payment Failed',
        pay: 'Pay Now'
      },
      auth: {
        login: 'Login',
        register: 'Sign Up',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        name: 'Full Name',
        googleLogin: 'Continue with Google',
        forgotPassword: 'Forgot password?',
        noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?',
        resetPassword: 'Reset Password',
        sendResetEmail: 'Send Reset Email',
        backToLogin: 'Back to Login',
        resetEmailSent: 'A password reset link has been sent to your email.',
        resetFailed: 'Failed to send reset email. Please try again.',
        fillAllFields: 'Please fill in all fields.',
        passwordsDontMatch: 'Passwords do not match.',
        passwordTooShort: 'Password must be at least 6 characters.',
        loginFailed: 'Failed to login. Please try again.',
        signupFailed: 'Failed to create account. Please try again.',
        googleFailed: 'Failed to login with Google. Please try again.',
        alreadyLoggedIn: 'You are redirecting back to your purchase...',
      },
      errors: {
        generic: 'Something went wrong. Please try again.',
        invalidCredential: 'Incorrect email or password.',
        userNotFound: 'No account found with this email.',
        invalidEmail: 'Please enter a valid email address.',
        userDisabled: 'This account has been disabled. Contact support.',
        emailInUse: 'This email is already registered. Try logging in.',
        weakPassword: 'Password is too weak. Use at least 6 characters.',
        operationNotAllowed: 'This sign-in method is not enabled.',
        tooManyRequests: 'Too many attempts. Please wait a moment and try again.',
        popupClosed: 'Login window was closed. Please try again.',
        popupBlocked: 'Login popup was blocked by your browser. Please allow popups and try again.',
        accountExistsDifferentCredential: 'An account already exists with the same email but a different sign-in method.',
        networkError: 'Connection error. Check your internet and try again.',
        expiredCode: 'This link has expired. Request a new one.',
        invalidCode: 'This link is invalid. Request a new one.',
        unauthorizedDomain: 'This domain is not authorized for login. Use rifa-maker.web.app or contact support.',
      },
      admin: {
        dashboard: 'Dashboard',
        totalRaffles: 'Total Raffles',
        totalRevenue: 'Total Revenue',
        totalUsers: 'Total Users',
        activeRaffles: 'Active Raffles',
        recentActivity: 'Recent Activity',
        manageRaffles: 'Manage Raffles',
        manageUsers: 'Manage Users'
      },
      common: {
        loading: 'Loading...',
        error: 'Something went wrong',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        search: 'Search',
        filter: 'Filter',
        sort: 'Sort',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        or: 'or',
        currency_brl: 'R$',
        currency_usd: '$'
      },
      footer: {
        rights: 'All rights reserved.',
        allRightsReserved: 'All rights reserved.',
        terms: 'Terms of Use',
        privacy: 'Privacy Policy',
        contact: 'Contact Us',
        description: 'The easiest way to create, share, and participate in raffles online.',
        legal: 'Legal',
        legalDisclaimer: 'RifaMaker is a technology platform that enables the creation of campaigns among friends and charitable fundraising. RifaMaker does not organize, manage, or promote sweepstakes, raffles, or lotteries. The responsibility for the legality of each campaign lies solely with its organizer.'
      },
      myRaffles: {
        myRafflesTab: 'My Raffles',
        myTicketsTab: 'My Tickets',
        pendingPayments: 'Pending Payments',
        manage: 'Manage',
        view: 'View',
        viewRaffle: 'View Raffle',
        noRafflesCreated: "You haven't created any raffles yet",
        createFirst: 'Create your first raffle to get started!',
        noTickets: "You haven't joined any raffles yet",
        browseRaffles: 'Browse available raffles and win amazing prizes!',
        noPending: 'No pending payments',
        allProcessed: 'All payments have been processed.',
        numbers: 'numbers',
        buyer: 'Buyer',
        confirm: 'Confirm',
        reject: 'Reject',
        confirmReject: 'Are you sure you want to reject this purchase? The numbers will be released.',
        noRafflesYet: 'No raffles yet — create the first one!',
        browseAvailable: 'Browse Raffles'
      },
      createRaffle: {
        pixTitle: 'PIX - Receive Payments',
        pixDescription: 'Buyers will send PIX payments directly to you. Enter your PIX key below.',
        pixKeyType: 'PIX Key Type',
        pixKey: 'PIX Key',
        imageOptional: 'Optional - adds a photo to your raffle',
        imageSelected: 'Selected',
        fillRequired: 'Please fill in all required fields.',
        pixRequired: 'Please enter your PIX key to receive payments.',
        pixInvalidCpf: 'Invalid CPF. Please enter 11 digits (no letters or @).',
        pixInvalidCnpj: 'Invalid CNPJ. Please enter 14 digits (no letters or @).',
        pixInvalidEmail: 'Invalid email. Please enter a valid email address.',
        pixInvalidPhone: 'Invalid phone number.',
        pixInvalidRandom: 'Invalid random key. It should be at least 20 characters.',
        pixInvalidCopiaECola: 'Invalid PIX Copia e Cola code. It must be at least 30 characters.',
        pixQrCodeRequired: 'Please upload a QR Code image.',
        pixQrCodeLabel: 'PIX QR Code Image',
        pixQrCodeHint: 'Click to upload your PIX QR Code image',
        pixCopiaECola: 'Copia e Cola',
        pixCopiaEColaHint: 'Paste the full PIX Copia e Cola code here...',
        pixRandom: 'Random Key',
        pixPhone: 'Phone',
        createFailed: 'Failed to create raffle. Please try again.',
        mediaGallery: 'Photos & Videos',
        mediaGalleryHint: 'Add up to 3 photos or videos to showcase your campaign. The first image will be used as the cover.',
        addMedia: 'Click to add photos or videos',
        mediaFormats: 'JPG, PNG, MP4, WebM (max 10MB)',
        maxMediaReached: 'You can add up to 3 files.',
        fileTooLarge: 'File too large. Maximum 10MB per file.',
        markdownHint: 'Tip: Use **bold**, *italic*, - bullet lists, and blank lines for paragraphs.'
      },
      cookies: {
        title: 'Cookie Notice',
        message: 'We use cookies to keep your session active and improve your experience. Essential cookies are required for the platform to work. Analytics cookies help us improve our services.',
        learnMore: 'Privacy Policy',
        acceptAll: 'Accept All',
        essentialOnly: 'Essential Only'
      },
      legal: {
        participantDisclaimer: 'This campaign is organized by the creator listed above. RifaMaker is a technology platform and does not organize or manage campaigns. By participating, you acknowledge that all transactions are between you and the campaign organizer.',
        reportCampaign: 'Report this campaign',
        reportTitle: 'Report Campaign',
        reportReason: 'Reason for report',
        reportReasonFraud: 'Suspected fraud',
        reportReasonIllegal: 'Illegal or unauthorized activity',
        reportReasonMisleading: 'Misleading information',
        reportReasonOther: 'Other',
        reportDetails: 'Additional details',
        reportSubmit: 'Submit Report',
        reportSuccess: 'Report submitted successfully. We will review it shortly.',
        reportFailed: 'Failed to submit report. Please try again.',
        ageRestriction: 'You must be 18 years or older to participate.',
        termsAcceptRegister: 'By creating an account, you agree to our',
        termsAnd: 'and',
      }
    }
  },
  pt: {
    translation: {
      nav: {
        home: 'Início',
        raffles: 'Rifas',
        create: 'Criar Rifa',
        createRaffle: 'Criar Rifa',
        myRaffles: 'Minhas Rifas',
        admin: 'Admin',
        login: 'Entrar',
        register: 'Cadastrar',
        logout: 'Sair',
        profile: 'Perfil'
      },
      hero: {
        title: 'Crie Ações entre Amigos Incríveis!',
        subtitle: 'O jeito mais fácil de criar, compartilhar e participar de ações entre amigos. Escolha seus números da sorte e ganhe!',
        cta: 'Explorar Campanhas',
        createCta: 'Crie Sua Campanha'
      },
      features: {
        title: 'Por que escolher a RifaMaker?',
        easy: 'Super Fácil',
        easyDesc: 'Crie sua campanha em minutos. Defina os números, preço e prêmio — nós cuidamos do resto!',
        secure: 'Pagamentos Seguros',
        secureDesc: 'Pague com PIX diretamente. Rápido, seguro e fácil.',
        fun: 'Divertido & Social',
        funDesc: 'Compartilhe com amigos, acompanhe seus números e comemore vitórias com confete!'
      },
      raffle: {
        create: 'Criar Rifa',
        title: 'Título',
        description: 'Descrição',
        prize: 'Prêmio',
        prizeValue: 'Valor do Prêmio',
        ticketPrice: 'Preço do Bilhete',
        totalNumbers: 'Total de Números',
        unlimited: 'Ilimitado',
        limited: 'Limitado',
        numberType: 'Tipo de Número',
        maxNumbers: 'Máximo de Números',
        drawDate: 'Data do Sorteio',
        drawTime: 'Horário do Sorteio',
        status: 'Status',
        active: 'Ativa',
        completed: 'Concluída',
        pending: 'Pendente',
        selectNumbers: 'Selecione Seus Números',
        available: 'Disponível',
        selected: 'Selecionado',
        sold: 'Vendido',
        reserved: 'Reservado',
        buyTickets: 'Comprar Bilhetes',
        myNumbers: 'Meus Números',
        participants: 'Participantes',
        winner: 'Vencedor',
        drawNow: 'Sortear Agora',
        shareRaffle: 'Compartilhar Rifa',
        shareCampaign: 'Compartilhar Campanha',
        image: 'Imagem da Rifa',
        currency: 'Moeda'
      },
      payment: {
        title: 'Pagamento',
        method: 'Método de Pagamento',
        creditCard: 'Cartão de Crédito',
        pix: 'PIX',
        total: 'Total',
        processing: 'Processando...',
        success: 'Pagamento Realizado!',
        error: 'Pagamento Falhou',
        pay: 'Pagar Agora'
      },
      auth: {
        login: 'Entrar',
        register: 'Cadastrar',
        email: 'Email',
        password: 'Senha',
        confirmPassword: 'Confirmar Senha',
        name: 'Nome Completo',
        googleLogin: 'Continuar com Google',
        forgotPassword: 'Esqueceu a senha?',
        noAccount: 'Não tem uma conta?',
        hasAccount: 'Já tem uma conta?',
        resetPassword: 'Redefinir Senha',
        sendResetEmail: 'Enviar Link de Redefinição',
        backToLogin: 'Voltar para Login',
        resetEmailSent: 'Um link de redefinição de senha foi enviado para o seu e-mail.',
        resetFailed: 'Falha ao enviar e-mail de redefinição. Tente novamente.',
        fillAllFields: 'Por favor, preencha todos os campos.',
        passwordsDontMatch: 'As senhas não coincidem.',
        passwordTooShort: 'A senha deve ter pelo menos 6 caracteres.',
        loginFailed: 'Falha ao entrar. Tente novamente.',
        signupFailed: 'Falha ao criar conta. Tente novamente.',
        googleFailed: 'Falha ao entrar com Google. Tente novamente.',
        alreadyLoggedIn: 'Você está sendo redirecionado para sua compra...',
      },
      errors: {
        generic: 'Algo deu errado. Por favor, tente novamente.',
        invalidCredential: 'Email ou senha incorretos.',
        userNotFound: 'Nenhuma conta encontrada com este email.',
        invalidEmail: 'Por favor, insira um email válido.',
        userDisabled: 'Esta conta foi desativada. Entre em contato com o suporte.',
        emailInUse: 'Este email já está cadastrado. Tente fazer login.',
        weakPassword: 'Senha muito fraca. Use pelo menos 6 caracteres.',
        operationNotAllowed: 'Este método de login não está habilitado.',
        tooManyRequests: 'Muitas tentativas. Aguarde um momento e tente novamente.',
        popupClosed: 'A janela de login foi fechada. Tente novamente.',
        popupBlocked: 'O popup de login foi bloqueado pelo navegador. Permita popups e tente novamente.',
        accountExistsDifferentCredential: 'Já existe uma conta com este email mas com outro método de login.',
        networkError: 'Erro de conexão. Verifique sua internet e tente novamente.',
        expiredCode: 'Este link expirou. Solicite um novo.',
        invalidCode: 'Este link é inválido. Solicite um novo.',
        unauthorizedDomain: 'Este domínio não está autorizado para login. Use rifa-maker.web.app ou entre em contato com o suporte.',
      },
      admin: {
        dashboard: 'Painel',
        totalRaffles: 'Total de Rifas',
        totalRevenue: 'Receita Total',
        totalUsers: 'Total de Usuários',
        activeRaffles: 'Rifas Ativas',
        recentActivity: 'Atividade Recente',
        manageRaffles: 'Gerenciar Rifas',
        manageUsers: 'Gerenciar Usuários'
      },
      common: {
        loading: 'Carregando...',
        error: 'Algo deu errado',
        save: 'Salvar',
        cancel: 'Cancelar',
        delete: 'Excluir',
        edit: 'Editar',
        search: 'Buscar',
        filter: 'Filtrar',
        sort: 'Ordenar',
        back: 'Voltar',
        next: 'Próximo',
        previous: 'Anterior',
        or: 'ou',
        currency_brl: 'R$',
        currency_usd: '$'
      },
      footer: {
        rights: 'Todos os direitos reservados.',
        allRightsReserved: 'Todos os direitos reservados.',
        terms: 'Termos de Uso',
        privacy: 'Política de Privacidade',
        contact: 'Fale Conosco',
        description: 'A forma mais fácil de criar, compartilhar e participar de ações entre amigos online.',
        legal: 'Legal',
        legalDisclaimer: 'A RifaMaker é uma plataforma tecnológica que possibilita a criação de ações entre amigos e campanhas de arrecadação beneficentes. A RifaMaker não organiza, gere ou promove sorteios, rifas ou loterias. A responsabilidade pela legalidade de cada campanha é exclusivamente do organizador.'
      },
      myRaffles: {
        myRafflesTab: 'Minhas Rifas',
        myTicketsTab: 'Meus Bilhetes',
        pendingPayments: 'Pagamentos Pendentes',
        manage: 'Gerenciar',
        view: 'Ver',
        viewRaffle: 'Ver Rifa',
        noRafflesCreated: 'Você ainda não criou nenhuma rifa',
        createFirst: 'Crie sua primeira rifa para começar!',
        noTickets: 'Você ainda não participou de nenhuma rifa',
        browseRaffles: 'Explore as rifas disponíveis e ganhe prêmios incríveis!',
        noPending: 'Nenhum pagamento pendente',
        allProcessed: 'Todos os pagamentos foram processados.',
        numbers: 'números',
        buyer: 'Comprador',
        confirm: 'Confirmar',
        reject: 'Rejeitar',
        confirmReject: 'Tem certeza que deseja rejeitar esta compra? Os números serão liberados.',
        noRafflesYet: 'Nenhuma rifa ainda — crie a primeira!',
        browseAvailable: 'Explorar Rifas'
      },
      createRaffle: {
        pixTitle: 'PIX - Receber Pagamentos',
        pixDescription: 'Os compradores enviarão pagamentos PIX diretamente para você. Insira sua chave PIX abaixo.',
        pixKeyType: 'Tipo da Chave PIX',
        pixKey: 'Chave PIX',
        imageOptional: 'Opcional - adiciona uma foto à sua rifa',
        imageSelected: 'Selecionado',
        fillRequired: 'Por favor, preencha todos os campos obrigatórios.',
        pixRequired: 'Por favor, insira sua chave PIX para receber pagamentos.',
        pixInvalidCpf: 'CPF inválido. Insira 11 dígitos (sem letras ou @).',
        pixInvalidCnpj: 'CNPJ inválido. Insira 14 dígitos (sem letras ou @).',
        pixInvalidEmail: 'E-mail inválido. Insira um endereço de e-mail válido.',
        pixInvalidPhone: 'Número de telefone inválido.',
        pixInvalidRandom: 'Chave aleatória inválida. Deve ter pelo menos 20 caracteres.',
        pixInvalidCopiaECola: 'Código PIX Copia e Cola inválido. Deve ter pelo menos 30 caracteres.',
        pixQrCodeRequired: 'Por favor, envie a imagem do QR Code PIX.',
        pixQrCodeLabel: 'Imagem do QR Code PIX',
        pixQrCodeHint: 'Clique para enviar a imagem do seu QR Code PIX',
        pixCopiaECola: 'Copia e Cola',
        pixCopiaEColaHint: 'Cole o código PIX Copia e Cola completo aqui...',
        pixRandom: 'Chave Aleatória',
        pixPhone: 'Telefone',
        createFailed: 'Falha ao criar rifa. Tente novamente.',
        mediaGallery: 'Fotos e Vídeos',
        mediaGalleryHint: 'Adicione até 3 fotos ou vídeos para mostrar sua campanha. A primeira imagem será usada como capa.',
        addMedia: 'Clique para adicionar fotos ou vídeos',
        mediaFormats: 'JPG, PNG, MP4, WebM (máx 10MB)',
        maxMediaReached: 'Você pode adicionar até 3 arquivos.',
        fileTooLarge: 'Arquivo muito grande. Máximo 10MB por arquivo.',
        markdownHint: 'Dica: Use **negrito**, *itálico*, - listas e linhas em branco para parágrafos.'
      },
      cookies: {
        title: 'Aviso de Cookies',
        message: 'Utilizamos cookies para manter sua sessão ativa e melhorar sua experiência. Cookies essenciais são necessários para o funcionamento da plataforma. Cookies analíticos nos ajudam a melhorar nossos serviços.',
        learnMore: 'Política de Privacidade',
        acceptAll: 'Aceitar Todos',
        essentialOnly: 'Apenas Essenciais'
      },
      legal: {
        participantDisclaimer: 'Esta campanha é organizada pelo criador indicado acima. A RifaMaker é uma plataforma tecnológica e não organiza ou gere campanhas. Ao participar, você reconhece que todas as transações são entre você e o organizador da campanha.',
        reportCampaign: 'Denunciar esta campanha',
        reportTitle: 'Denunciar Campanha',
        reportReason: 'Motivo da denúncia',
        reportReasonFraud: 'Suspeita de fraude',
        reportReasonIllegal: 'Atividade ilegal ou não autorizada',
        reportReasonMisleading: 'Informações enganosas',
        reportReasonOther: 'Outro',
        reportDetails: 'Detalhes adicionais',
        reportSubmit: 'Enviar Denúncia',
        reportSuccess: 'Denúncia enviada com sucesso. Analisaremos em breve.',
        reportFailed: 'Falha ao enviar denúncia. Tente novamente.',
        ageRestriction: 'Você deve ter 18 anos ou mais para participar.',
        termsAcceptRegister: 'Ao criar uma conta, você concorda com nossos',
        termsAnd: 'e',
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'pt',
    lng: 'pt',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
      caches: ['localStorage']
    }
  });

export default i18n;
